/**
 * LearningCenterPage — the operator-facing Learning Center of one Digital Employee
 * (DIGEMP-LEARNING-CENTER-FRONTEND-054), at /agents/:id/learning-center.
 *
 * One screen answering "what did this employee learn and how do we know": a merged timeline of
 * verified outcomes, technician feedback and lessons, knowledge candidates (own-run KnowledgeGaps),
 * proficiency movement and reversible changes. Every record is read from an existing authoritative,
 * already-permission-gated endpoint — GET /api/agents/:id/experience-history and
 * GET /api/agents/:id/growth-profile (both under the same agents_view gate as the employee page)
 * and the existing activity log — and nothing here writes, executes, approves or grants anything.
 *
 * Truthful degradation is a requirement, not a nicety: a source that failed says so, a source
 * still loading says so, an employee without finished runs is "טרם נמדד" (never 0), and the
 * activity-log scan width is disclosed so an old change outside the window is never mistaken
 * for "no change".
 */
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentsApi, activityApi } from "../services/api";
import LearningTimeline, {
  OUTCOME_META,
  REVERSIBLE_CHANGE_ACTIONS,
  buildLearningEntries,
  normalizeVersionedSkillHistory,
  SkillVersionHistorySection,
} from "../components/LearningTimeline";
import EmployeeAvatar from "../components/EmployeeAvatar";

// The activity log API caps limit at 500; a full window is disclosed in the UI.
export const ACTIVITY_WINDOW = 500;
export const MOVEMENT_MIN_ENTRIES = 6;
export const EXPERIENCE_LIMIT = 50;

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("he-IL"); } catch { return ""; }
}

function fmtDateOnly(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("he-IL"); } catch { return ""; }
}

/**
 * Movement derived only from the audited outcome window: verified share of the earlier vs the
 * later half of the chronological sequence. Returns null below the minimum sample — a two-entry
 * "trend" would be a claim the evidence cannot carry.
 */
export function movementSummary(orderedEntries) {
  const seq = Array.isArray(orderedEntries) ? orderedEntries : [];
  if (seq.length < MOVEMENT_MIN_ENTRIES) return null;
  const mid = Math.floor(seq.length / 2);
  const older = seq.slice(0, mid);
  const newer = seq.slice(mid);
  const share = (arr) =>
    Math.round((arr.filter((e) => e.outcome === "verified_success").length / arr.length) * 100);
  return { olderShare: share(older), newerShare: share(newer), olderCount: older.length, newerCount: newer.length };
}

function Tile({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border shadow-sm p-3 space-y-0.5">
      <div className="text-lg font-bold text-gray-800">{value}</div>
      <div className="text-[11px] text-gray-500">{label}</div>
      {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
    </div>
  );
}

export default function LearningCenterPage() {
  const { id } = useParams();

  const { data: agent, isLoading: agentLoading, error: agentError } = useQuery({
    queryKey: ["agent", id],
    queryFn: () => agentsApi.get(id),
    enabled: !!id,
  });

  const { data: experience, isLoading: experienceLoading, isError: experienceFailed } = useQuery({
    queryKey: ["agent-experience-history", id],
    queryFn: () => agentsApi.experienceHistory(id, { limit: EXPERIENCE_LIMIT }),
    enabled: !!id,
    staleTime: 60_000,
  });

  const { data: profile, isLoading: profileLoading, isError: profileFailed } = useQuery({
    queryKey: ["agent-growth-profile", id],
    queryFn: () => agentsApi.growthProfile(id),
    enabled: !!id,
    staleTime: 60_000,
  });

  const { data: activityRows = [], isLoading: changesLoading, isError: changesFailed } = useQuery({
    queryKey: ["agent-changes-window", id],
    queryFn: () => activityApi.list({ entity_type: "agent", limit: ACTIVITY_WINDOW }),
    enabled: !!id,
    staleTime: 60_000,
  });

  const changesWindowFull = Array.isArray(activityRows) && activityRows.length >= ACTIVITY_WINDOW;
  const agentChanges = useMemo(
    () =>
      (Array.isArray(activityRows) ? activityRows : []).filter(
        (row) => row?.entity_id === id && REVERSIBLE_CHANGE_ACTIONS.has(row?.action),
      ),
    [activityRows, id],
  );

  const skillHistory = useMemo(
    () => normalizeVersionedSkillHistory(agent?.learning_history),
    [agent],
  );

  const entries = useMemo(
    () =>
      buildLearningEntries({
        experience,
        goals: profile?.learning_goals,
        changes: agentChanges,
        skillChanges: skillHistory?.changes,
      }),
    [experience, profile, agentChanges, skillHistory],
  );

  const orderedOutcomes = useMemo(
    () => entries.filter((entry) => entry.category === "outcome").slice().reverse(),
    [entries],
  );
  const movement = useMemo(() => movementSummary(orderedOutcomes), [orderedOutcomes]);

  if (agentLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (!agent) {
    const refused = agentError?.response?.status === 403;
    return (
      <div className="text-center py-16" dir="rtl">
        <p className="text-red-600 text-sm">
          {refused ? "אין הרשאה לצפות בעובד זה" : "לא ניתן לטעון את העובד"}
        </p>
        <Link to="/agents" className="text-xs text-brand-600 hover:underline mt-2 block">← חזרה לסוכנים</Link>
      </div>
    );
  }

  const summary = experience?.summary || null;
  const proficiency = profile?.evaluated_proficiency || null;
  const goals = Array.isArray(profile?.learning_goals) ? profile.learning_goals : [];
  const terminalOutcomes = orderedOutcomes.length;

  const outcomeCounts = orderedOutcomes.reduce(
    (acc, entry) => {
      if (entry.outcome && acc[entry.outcome] != null) acc[entry.outcome] += 1;
      return acc;
    },
    { verified_success: 0, unverified: 0, failed: 0, cancelled: 0 },
  );

  return (
    <div className="space-y-5 max-w-5xl mx-auto" dir="rtl">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
          <Link to="/agents" className="hover:text-gray-600">סוכנים</Link>
          <span>/</span>
          <Link to={`/agents/${id}`} className="hover:text-gray-600">{agent.name}</Link>
          <span>/</span>
          <span className="text-gray-600">מרכז הלמידה</span>
        </div>
        <div className="flex items-center gap-3">
          <EmployeeAvatar name={agent.name} avatarKey={agent.avatar_key} avatarColor={agent.avatar_color} size="lg" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">🎓 מרכז הלמידה — {agent.name}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              תוצאות מאומתות, משוב טכנאים, מועדי ידע, גרסאות מיומנות, תנועת מיומנות ושינויים הפיכים —
              מבוסס ראיות מתועדות בלבד. המסך אינו מעניק הרשאות ואינו מבצע פעולות.
            </p>
          </div>
        </div>
      </div>

      {/* Evidence summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile
          label="עבודות מאומתות"
          value={experienceFailed ? "—" : (summary ? summary.verified_successes : "…")}
          sub={summary ? `מתוך ${summary.significant_entries} עבודות משמעותיות` : "אימות טכנאי בלבד"}
        />
        <Tile
          label="טרם אומתו"
          value={experienceFailed ? "—" : (summary ? summary.unverified_completions : "…")}
          sub="הסתיימו בלי אימות טכנאי"
        />
        <Tile
          label="נכשלו / בוטלו"
          value={experienceFailed ? "—" : (summary ? summary.failures + summary.cancellations : "…")}
          sub={summary ? `${summary.failures} כשלו · ${summary.cancellations} בוטלו` : ""}
        />
        <Tile
          label="מועדי ידע"
          value={profileFailed ? "—" : (profile ? goals.length : "…")}
          sub="פערי ידע מהריצות של העובד"
        />
        <Tile
          label="ציון מוערך"
          value={
            profileFailed ? "—"
            : proficiency && proficiency.has_outcome_evidence ? Math.round(proficiency.overall_score)
            : profile ? "טרם נמדד" : "…"
          }
          sub="מתוך ריצות שהסתיימו"
        />
      </div>

      {/* Proficiency movement */}
      <section aria-label="תנועת מיומנות" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-700">תנועת מיומנות</h2>
          {proficiency?.evaluated_at && (
            <span className="text-[11px] text-gray-400">נמדד: {fmt(proficiency.evaluated_at)}</span>
          )}
        </div>

        {profileFailed ? (
          <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
            לא ניתן לטעון את פרופיל הלמידה
          </p>
        ) : profileLoading || !profile ? (
          <p className="text-sm text-gray-400 italic">טוען את פרופיל הלמידה...</p>
        ) : proficiency && proficiency.has_outcome_evidence ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-brand-600">{Math.round(proficiency.overall_score)}</div>
                <div className="text-[11px] text-gray-400">ציון מוערך כולל</div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center text-xs flex-1 min-w-[16rem]">
                {[
                  { label: "שיעור פתרון", value: `${Math.round((proficiency.resolution_rate || 0) * 100)}%` },
                  { label: "ריצות שהסתיימו", value: proficiency.evidence?.terminal_runs ?? "—" },
                  { label: "נכשלו", value: proficiency.evidence?.failed_runs ?? "—" },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-gray-50 rounded-lg py-2">
                    <div className="font-semibold text-gray-700">{value}</div>
                    <div className="text-gray-400 mt-0.5">{label}</div>
                  </div>
                ))}
              </div>
            </div>
            {proficiency.by_category?.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs text-gray-500 font-medium">פילוח לפי קטגוריה</p>
                {proficiency.by_category.map((entry) => (
                  <div key={entry.category} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-28 truncate" title={entry.category}>{entry.category}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                      <div className="h-1.5 rounded-full bg-brand-400" style={{ width: `${Math.round(entry.completion_rate * 100)}%` }} />
                    </div>
                    <span className="text-xs text-gray-400 w-16 text-left">{entry.completed}/{entry.total}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">טרם נמדד — אין עדיין ריצות שהסתיימו להערכה</p>
        )}

        {experienceFailed ? (
          <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
            לא ניתן לטעון את רצף התוצאות
          </p>
        ) : experienceLoading && orderedOutcomes.length === 0 ? (
          <p className="text-sm text-gray-400 italic">טוען את רצף התוצאות...</p>
        ) : terminalOutcomes === 0 ? (
          <p className="text-sm text-gray-400 italic">אין עדיין עבודות שהסתיימו להצגת רצף</p>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-gray-500 font-medium">רצף התוצאות לפי זמן (ישן → חדש) · {terminalOutcomes} עבודות</p>
            <div className="flex flex-wrap gap-1" role="list" aria-label="רצף התוצאות המבוקר לפי זמן">
              {orderedOutcomes.map((entry) => {
                const meta = OUTCOME_META[entry.outcome];
                const tip = `${meta.label} · ${fmtDateOnly(entry.at)}${entry.ticket?.number ? ` · קריאה #${entry.ticket.number}` : ""}`;
                return (
                  <span
                    key={entry.id}
                    role="listitem"
                    title={tip}
                    aria-label={tip}
                    className={`w-4 h-4 rounded-sm ${
                      entry.outcome === "verified_success" ? "bg-green-500"
                      : entry.outcome === "unverified" ? "bg-amber-400"
                      : entry.outcome === "failed" ? "bg-red-500"
                      : "bg-slate-400"
                    }`}
                  />
                );
              })}
            </div>
            <p className="text-[11px] text-gray-500">
              מאומתות {outcomeCounts.verified_success} · טרם אומתו {outcomeCounts.unverified} · נכשלו {outcomeCounts.failed} · בוטלו {outcomeCounts.cancelled}
            </p>
            {movement ? (
              <p className="text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded px-2 py-1">
                שיעור העבודות המאומתות בחלון שנטען: {movement.olderShare}% במחצית המוקדמת
                ({movement.olderCount} עבודות) מול {movement.newerShare}% במחצית המאוחרת ({movement.newerCount} עבודות)
                — נגזר מרצף העבודות המבוקר, אינו מדד רשמי
              </p>
            ) : (
              <p className="text-xs text-gray-400">
                אין עדיין די עבודות בחלון לחישוב השוואה ({terminalOutcomes} מתוך {MOVEMENT_MIN_ENTRIES} נדרשות)
              </p>
            )}
          </div>
        )}

        <p className="text-[11px] text-gray-400">
          ההערכה מחושבת ממדדים דטרמיניסטיים של הריצות שהסתיימו (בסיס: agent_run_history) ואינה
          מוצגת כערך לפני שיש ראיות. אין במערכת היסטוריית ציונים מספרית — המסלול המתועד שמסביר כל
          שינוי מצב מיומנות (קידום, ירידה, השהיה) הוא יומן הגרסאות למטה; השוואת המחציות שלמעלה היא
          הערכה גסה שנגזרת מרצף העבודות בלבד.
        </p>
      </section>

      {/* Versioned skill history — what changed, why, and on what evidence */}
      <SkillVersionHistorySection
        history={agent.learning_history}
        isLoading={agentLoading}
        isError={!!agentError}
      />

      {/* Unified timeline */}
      <LearningTimeline
        entries={entries}
        isLoading={experienceLoading || profileLoading || changesLoading}
        isError={experienceFailed || profileFailed || changesFailed}
        agentId={id}
      />

      {changesWindowFull && (
        <p className="text-[11px] text-gray-400">
          יומן הפעילות נסרק עד {ACTIVITY_WINDOW} האירועים האחרונים של כל העובדים — ייתכן ששינויים
          ישנים יותר של עובד זה אינם מופיעים.
        </p>
      )}

      {/* Sources / freshness */}
      <div className="text-[11px] text-gray-400 space-y-0.5">
        {experience?.generated_at && <div>היסטוריית ניסיון: נטענה {fmt(experience.generated_at)} (עד {experience.limit} עבודות אחרונות)</div>}
        {profile?.generated_at && <div>פרופיל למידה: נטען {fmt(profile.generated_at)}</div>}
        <div>כל הרשומות מגיעות מהרשומות המבוקרות הקיימות במערכת — ללא זיכרון מודל, ללא מטעני גלם וללא סודות.</div>
      </div>

      <div>
        <Link to={`/agents/${id}`} className="text-xs text-brand-600 hover:underline">← חזרה לעמוד העובד</Link>
      </div>
    </div>
  );
}
